import React, { useEffect, useState } from 'react';
import CalendarHealthPlanEventForm, {
	CalendarHealthPlanEventFormValues,
} from './CalendarHealthPlanEventForm';
import PatientMiniProfile from '../../../PatientMiniProfile/PatientMiniProfile';
import { useAppContext } from '../../../../Services/AppContext';
import { useEffectAsync } from '../../../../helpers';
import useUserRelations, {
	HealthPlanRelation,
} from '../../../../Modules/User/Hooks/useUserRelations';
import { HealthPlanCode } from '../../../../Enums/HealthPlanCode.enum';
import { Moment } from 'moment';
import apiClient, { IResponse } from '../../../../Services/ApiClient';
import useNotification from '../../../../Hooks/useNotification.hook';
import { getMomentDate } from '../../../../Utils/dates.util';
import useTranslation from '../../../../Hooks/useTranslation';
import OverlayLoading from '../../../OverlayLoading';
import useHealthPlanAppointment from '../../../../Modules/HealthPlan/Services/healthPlanAppointment.service';
import CalendarHealthPlanEventAppointmentInfo from './CalendarHealthPlanEventAppointmentInfo';
import { useHealthPlanContext } from '../../../../Services/HealthPlanContext';
import {
	useLazyEventsQuery,
	useLazyWorkplaceCaseTypeQuery,
} from '../../../../graphql/graphql';

interface Props {
	patientId: string;
	workplaceId: string;
	healthPlanCode: HealthPlanCode;
	date?: Moment | null;
	timeFrom?: Moment | null;
	onEventCreate: (response: IResponse) => void;
	onEventEdit: (response: IResponse) => void;
	event?: any;
}

const CalendarHealthPlanEvent: React.FC<Props> = ({
	patientId,
	workplaceId,
	healthPlanCode,
	date,
	timeFrom,
	onEventCreate,
	event,
}) => {
	const [defaultValues, setDefaultValues] = useState({});
	const [caseTypeId, setCaseTypeId] = useState<string | null>(null);
	const [isLoading, setIsLoading] = useState(false);
	const [isFirstMeeting, setIsFirstMeeting] = useState(true);
	const { userData } = useAppContext();
	const { getHealthPlanDoctorId, getHealthPlanRelationCodeByHealthPlanCode } =
		useUserRelations();
	const { showNotificationSuccess } = useNotification();
	const { t } = useTranslation();
	const nextAppointment = useHealthPlanAppointment().getNextAppointmentInfo();
	const { healthPlanUser } = useHealthPlanContext();
	const [getWorkplaceCaseType] = useLazyWorkplaceCaseTypeQuery();
	const [getEvents] = useLazyEventsQuery();

	console.log('event', event);

	useEffect(() => {
		if (event) {
			if (event?.event?.start) {
				setDefaultValues(prevValues => {
					return {
						...prevValues,
						date: getMomentDate(event?.event?.start),
						timeFrom: getMomentDate(event?.event?.start),
					};
				});
			}
			if (event?.event?.end) {
				setDefaultValues(prevValues => {
					return {
						...prevValues,
						timeTo: getMomentDate(event?.event?.end),
					};
				});
			}
			if (event?.event?.extendedProps?.note) {
				setDefaultValues(prevValues => {
					return {
						...prevValues,
						note: event.event.extendedProps.note,
					};
				});
			}
			if (event?.event?.extendedProps?.doctor?.id) {
				setDefaultValues(prevValues => {
					return {
						...prevValues,
						medicalProfessional: {
							value: event.event.extendedProps.doctor.id,
						},
					};
				});
			}
			if (event?.event?.extendedProps?.meta) {
				const meetingType = event?.event?.extendedProps?.meta.find(
					({ key }: any) => key === 'meetingType',
				);
				const paidMeeting = event?.event?.extendedProps?.meta.find(
					({ key }: any) => key === 'paidMeeting',
				);
				const caseType = event?.event?.extendedProps?.meta.find(
					({ key }: any) => key === 'caseType',
				);

				if (meetingType) {
					let label = '';
					if (meetingType.value === 'telemedicine') {
						label = t('base.meetingType.telemedicine') ?? '';
					}
					if (meetingType.value === 'onSite') {
						label = t('base.meetingType.onSite') ?? '';
					}
					setDefaultValues(prevValues => {
						return {
							...prevValues,
							meetingType: {
								label: label,
								value: meetingType.value,
							},
						};
					});
				}

				if (paidMeeting) {
					setDefaultValues(prevValues => {
						return {
							...prevValues,
							paidMeeting: paidMeeting.value,
						};
					});
				}

				if (caseType) {
					setCaseTypeId(caseType.value);
				}
			}
		}
	}, [event]);

	useEffect(() => {
		if (!event) {
			if (date) {
				setDefaultValues(prevValues => {
					return {
						...prevValues,
						date,
					};
				});
			}
			if (timeFrom) {
				setDefaultValues(prevValues => {
					return {
						...prevValues,
						timeFrom,
					};
				});
			}
		}
	}, []);

	useEffectAsync(async () => {
		if (!event) {
			const healthPlanRelationCode: HealthPlanRelation | null =
				getHealthPlanRelationCodeByHealthPlanCode(healthPlanCode);
			if (healthPlanRelationCode) {
				const healthPlanDoctorId = await getHealthPlanDoctorId(
					patientId,
					workplaceId,
					healthPlanRelationCode,
				);
				if (healthPlanDoctorId) {
					setDefaultValues(prevValues => {
						return {
							...prevValues,
							medicalProfessional: {
								value: healthPlanDoctorId,
							},
						};
					});
				}
			}
		}
	});

	useEffectAsync(async () => {
		const events = await getEvents({
			workplaceId: workplaceId,
			userIds: [patientId],
			metaKey: healthPlanCode + '_first_appointment',
		}).unwrap();

		if (events?.events?.data && events?.events?.data?.length > 0) {
			setIsFirstMeeting(false);
		}
	}, []);

	useEffectAsync(async () => {
		if (!event) {
			const data = await getWorkplaceCaseType({
				workplaceId: workplaceId,
				caseTypeCode: isFirstMeeting
					? healthPlanCode + '_first_appointment'
					: healthPlanCode + '_appointment',
			}).unwrap();
			if (data?.workplaceCaseType?.id) {
				setCaseTypeId(data.workplaceCaseType.id);
			}
		}
	}, [isFirstMeeting]);

	const handleSubmitForm = async (
		values: CalendarHealthPlanEventFormValues,
	) => {
		setIsLoading(true);
		const timeFrom = getMomentDate(values.timeFrom);
		const timeTo = getMomentDate(values.timeTo);
		const day = getMomentDate(values.date);

		const meta = [];
		const eventMembers = [];

		meta.push({
			key: 'meetingType',
			value: values.meetingType.value,
		});

		meta.push({
			key: 'paidMeeting',
			value: values.paidMeeting ?? false,
		});

		if (caseTypeId) {
			meta.push({
				key: 'caseType',
				value: caseTypeId,
			});
		}

		if (isFirstMeeting) {
			meta.push({
				key: healthPlanCode + '_first_appointment',
				value: true,
			});
		}

		eventMembers.push({
			workplaceMemberId: values.medicalProfessional.value,
		});

		eventMembers.push({
			userId: patientId,
		});

		const eventData = {
			dateFrom: day
				.clone()
				.startOf('day')
				.add(timeFrom.format('HH'), 'hours')
				.add(timeFrom.format('mm'), 'minutes'),
			dateTo: day
				.clone()
				.startOf('day')
				.add(timeTo.format('HH'), 'hours')
				.add(timeTo.format('mm'), 'minutes'),
			type: 'default',
			eventMembers,
			meta,
			propIgnoreFreeTimeCheck: true,
			workplaceId: workplaceId,
			description: values.note ?? '',
			healthPlanUserItemId: nextAppointment?.id ?? null,
			healthPlanUserId: healthPlanUser?.id ?? null,
			ignoreMe: true,
		};

		const response = !event
			? await apiClient.post('/calendar/event/', eventData)
			: await apiClient.patch('/event/' + event.event.id, eventData);

		setIsLoading(false);

		if (!response.data?.error) {
			showNotificationSuccess({
				content: t('base.shared.patientOrdered'),
			});
			response.data['isFirstMeeting'] = isFirstMeeting;
			onEventCreate(response);
		}
	};

	return (
		<div>
			{isLoading && <OverlayLoading />}
			<PatientMiniProfile
				userId={patientId}
				workplaceId={workplaceId}
				loggedUserId={userData.id}
				showVerifyInformation={false}
				links={null}
				showAge={true}
				showBirthDate={false}
				showAddress={false}
				showPhone={false}
				showEmail={false}
				showInsuranceCompany={false}
				showGender={false}
			/>
			<CalendarHealthPlanEventAppointmentInfo healthPlanCode={healthPlanCode} />
			<div style={{ marginTop: '20px' }}>
				<CalendarHealthPlanEventForm
					onSubmit={handleSubmitForm}
					defaultValues={defaultValues}
					patientId={patientId}
				/>
			</div>
		</div>
	);
};

export default CalendarHealthPlanEvent;
